import React, { type ReactElement } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import { isNonMobileWeb, useWindowDimensions, View, YStack } from 'smoothie';

import { ErrorView } from './components/ErrorView';
import { LoadingView } from './components/LoadingView';
import { ProfileHeader } from './ProfileHeader';
import { useFlow } from './providers/FlowProvider';
import { useFlowRouter } from './providers/FlowRouter';

interface BaseFlowProps {
    tabBar: ReactElement;
}

export const BaseFlow = ({ tabBar }: BaseFlowProps) => {
    const { index, setIndex, isSubmitting, onExit } = useFlow();
    const { steps, routes } = useFlowRouter();
    const { width: windowWidth } = useWindowDimensions();

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: string } }) => {
        if (isSubmitting) {
            return <LoadingView />;
        }

        if (routes[index]?.key !== route.key) {
            return null;
        }

        const StepComponent = steps[route.key];

        if (StepComponent == null) {
            return <ErrorView onExit={onExit} />;
        }

        return StepComponent();
    };

    return (
        <YStack
            $sm={{
                width: '100%',
                maxWidth: 600,
                alignSelf: 'center',
            }}
            height="100%"
        >
            <TabView
                lazy={true}
                renderLazyPlaceholder={() => <LoadingView />}
                swipeEnabled={false}
                navigationState={{ index, routes }}
                onIndexChange={setIndex}
                renderScene={renderScene}
                renderTabBar={() => tabBar}
                initialLayout={{
                    width: isNonMobileWeb ? 600 : windowWidth,
                }} // https://reactnavigation.org/docs/tab-view/#initiallayout
            />
        </YStack>
    );
};

interface MemberProfileFlowProps {
    tabBarOverride?: ReactElement;
}

// A Wrapper around BaseFlow with a default stylized TabBar.
export const MemberProfileFlow = ({
    tabBarOverride,
}: MemberProfileFlowProps) => {
    const { onBack } = useFlowRouter();

    return (
        <BaseFlow
            tabBar={
                tabBarOverride ?? (
                    <View
                        marginHorizontal={16}
                        marginTop={20}
                        $sm={{ marginHorizontal: 0 }}
                    >
                        <ProfileHeader customBackFn={onBack} />
                    </View>
                )
            }
        />
    );
};
